import React, { useEffect, useRef } from "react";

/**
 * BondNetwork — a subtle canvas behind the hero.
 *
 * Drifting "peers" (dots) that occasionally form a brief, glowing "bond"
 * between two of them. Wordlessly suggests a living peer-to-peer network.
 * Intentionally quiet — the hero copy stays the star.
 */

const ACCENT = "30,136,229"; // BetBond blue, as r,g,b for rgba()

const config = {
  peerCount: 28,
  driftSpeed: 0.12,        // px / frame
  peerRadius: 1.6,
  peerColor: `rgba(${ACCENT}, 0.55)`,
  peerHaloColor: `rgba(${ACCENT}, 0.12)`,
  bondMinDistance: 90,     // px — only bond peers reasonably close
  bondMaxDistance: 260,
  bondDurationMs: 1400,
  bondSpawnEveryMs: [900, 1800], // random interval between new bonds
  maxConcurrentBonds: 4,
};

const rand = (min, max) => Math.random() * (max - min) + min;

const BondNetwork = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const peersRef = useRef([]);
  const bondsRef = useRef([]);
  const lastBondSpawnRef = useRef(0);
  const nextBondDelayRef = useRef(1000);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let dpr = Math.max(1, window.devicePixelRatio || 1);
    let width = 0;
    let height = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const seedPeers = () => {
      peersRef.current = Array.from({ length: config.peerCount }, () => ({
        x: rand(0, width),
        y: rand(0, height),
        vx: rand(-config.driftSpeed, config.driftSpeed),
        vy: rand(-config.driftSpeed, config.driftSpeed),
        r: rand(config.peerRadius * 0.75, config.peerRadius * 1.4),
      }));
    };

    const trySpawnBond = (now) => {
      if (bondsRef.current.length >= config.maxConcurrentBonds) return;
      if (now - lastBondSpawnRef.current < nextBondDelayRef.current) return;

      const peers = peersRef.current;
      const tries = 8;
      for (let i = 0; i < tries; i++) {
        const a = Math.floor(Math.random() * peers.length);
        let b = Math.floor(Math.random() * peers.length);
        if (a === b) b = (b + 1) % peers.length;
        const pa = peers[a];
        const pb = peers[b];
        const dx = pa.x - pb.x;
        const dy = pa.y - pb.y;
        const dist = Math.hypot(dx, dy);
        if (dist >= config.bondMinDistance && dist <= config.bondMaxDistance) {
          bondsRef.current.push({
            a,
            b,
            start: now,
            duration: config.bondDurationMs,
          });
          lastBondSpawnRef.current = now;
          nextBondDelayRef.current = rand(
            config.bondSpawnEveryMs[0],
            config.bondSpawnEveryMs[1]
          );
          return;
        }
      }
    };

    const draw = (now) => {
      if (!width || !height) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, width, height);

      const peers = peersRef.current;
      for (const p of peers) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;
      }

      trySpawnBond(now);

      // bonds
      bondsRef.current = bondsRef.current.filter(
        (bond) => now - bond.start < bond.duration
      );
      for (const bond of bondsRef.current) {
        const pa = peers[bond.a];
        const pb = peers[bond.b];
        if (!pa || !pb) continue;
        const t = (now - bond.start) / bond.duration; // 0..1
        // ease-in-out alpha (fade in then out)
        const alpha = Math.sin(Math.PI * t);
        ctx.strokeStyle = `rgba(${ACCENT}, ${0.55 * alpha})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();

        // soft glow on endpoints
        for (const p of [pa, pb]) {
          const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 14);
          g.addColorStop(0, `rgba(${ACCENT}, ${0.35 * alpha})`);
          g.addColorStop(1, `rgba(${ACCENT}, 0)`);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 14, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // peers
      for (const p of peers) {
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 8);
        g.addColorStop(0, config.peerHaloColor);
        g.addColorStop(1, `rgba(${ACCENT}, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = config.peerColor;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    seedPeers();

    if (prefersReducedMotion) {
      // single static frame
      draw(performance.now());
      return () => {};
    }

    rafRef.current = requestAnimationFrame(draw);

    let resizeRaf = 0;
    const onResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        resize();
        seedPeers();
        bondsRef.current = [];
      });
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(resizeRaf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={`pointer-events-none absolute inset-0 w-full h-full ${className}`}
    />
  );
};

export default BondNetwork;
